import React, { useState } from "react";
import api from "../lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Upload, Truck } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable dialog that uploads a transport copy (lorry receipt / courier note)
 * and auto-attaches it to an order via the invoice number the staffer types.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   defaultInvoiceNumber?: string  // pre-fill when opening from a row
 *   onUploaded?: (response) => void  // parent refresh hook
 */
export default function TransportCopyUploadDialog({ open, onOpenChange, defaultInvoiceNumber = "", onUploaded }) {
    const [invoiceNumber, setInvoiceNumber] = useState(defaultInvoiceNumber);
    const [transporter, setTransporter] = useState("");
    const [lrNumber, setLrNumber] = useState("");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    React.useEffect(() => {
        if (open) {
            setInvoiceNumber(defaultInvoiceNumber || "");
            setTransporter(""); setLrNumber(""); setNotes(""); setFile(null);
        }
    }, [open, defaultInvoiceNumber]);

    const pick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 6 * 1024 * 1024) {
            toast.error(`${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB. Max is 6 MB.`);
            e.target.value = "";
            return;
        }
        setFile(f);
    };

    const submit = async () => {
        if (!invoiceNumber.trim()) return toast.error("Enter the bill / invoice number this transport copy is for");
        if (!file) return toast.error("Attach the transport copy file (PDF or image)");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("invoice_number", invoiceNumber.trim());
            fd.append("transporter", transporter.trim());
            fd.append("lr_number", lrNumber.trim());
            fd.append("notes", notes.trim());
            fd.append("file", file);
            const { data } = await api.post("/orders-transport-copy", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Transport copy attached to order #${data.order_id.slice(-8).toUpperCase()} · invoice ${data.invoice_number}`);
            onOpenChange(false);
            onUploaded?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-lg" data-testid="transport-upload-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-stone-900 flex items-center gap-2">
                        <Truck className="w-5 h-5 text-sky-700" />
                        Upload transport copy
                    </DialogTitle>
                    <DialogDescription>
                        Provide the bill number — we'll auto-attach this transport copy to the matching order so the distributor sees it in their portal.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Invoice / bill number *</Label>
                        <Input
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            placeholder="e.g. HPL-2026-00007"
                            className="rounded-none border-stone-300 mt-1 font-mono"
                            data-testid="tc-invoice-number"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Transporter</Label>
                            <Input
                                value={transporter}
                                onChange={(e) => setTransporter(e.target.value)}
                                placeholder="e.g. VRL Logistics"
                                className="rounded-none border-stone-300 mt-1"
                                data-testid="tc-transporter"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">LR number</Label>
                            <Input
                                value={lrNumber}
                                onChange={(e) => setLrNumber(e.target.value)}
                                placeholder="e.g. LR/2026/4421"
                                className="rounded-none border-stone-300 mt-1 font-mono"
                                data-testid="tc-lr-number"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. 3 boxes, fragile"
                            className="rounded-none border-stone-300 mt-1"
                            data-testid="tc-notes"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Transport copy file *</Label>
                        <label className="mt-1 flex items-center gap-2 border border-dashed border-stone-300 px-3 py-3 cursor-pointer hover:border-sky-500 hover:bg-sky-50 transition-colors">
                            <Upload className="w-4 h-4 text-stone-500" />
                            <span className="text-sm text-stone-600">{file ? file.name : "Click to pick a PDF or image (max 6 MB)"}</span>
                            <input type="file" accept=".pdf,image/*" className="hidden" onChange={pick} data-testid="tc-file" />
                        </label>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} disabled={busy} data-testid="tc-cancel">Cancel</Button>
                    <Button className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" onClick={submit} disabled={busy} data-testid="tc-submit">
                        {busy ? "Uploading…" : "Upload & attach"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
